import { useMemo } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  WorkflowAction,
  TranslationObject,
  getEntityOperationConfig,
} from '@web-config-app/core';
import { useConfigUserFeedback } from '@web-config-app/core-react';
import { useEntityFormError } from '../use-entity-form-error/use-entity-form-error.hook';
import type { EntityWorkflowActionsProps } from '../../types/hooks';

export const usePrimaryAction = ({
  operations,
  status,
  isEntitySaved,
  entityConfigName,
  environmentName,
  publishDangerously,
  isReadOnly,
  domainEntity,
  isDeepPublishAvailable,
  setIsDeepPublishModalOpen,
}: EntityWorkflowActionsProps & {
  isDeepPublishAvailable?: boolean;
  setIsDeepPublishModalOpen?: (isOpen: boolean) => void;
}): WorkflowAction => {
  const { formatMessage } = useIntl();
  const { validateEntityData } = useEntityFormError();
  const { setErrorFilterKeywordsActionOverride } = useConfigUserFeedback(
    (state) => ({
      setErrorFilterKeywordsActionOverride:
        state.setErrorFilterKeywordsActionOverride,
    }),
  );
  const getActionLabel = (
    defaultLabel: string,
    labelConfig?: TranslationObject,
  ) => {
    if (labelConfig?.text) {
      return labelConfig.text;
    }

    return formatMessage({ id: labelConfig?.key ?? defaultLabel });
  };
  const validateBeforePublish = () => {
    setErrorFilterKeywordsActionOverride([]);
    return validateEntityData({ filteredErrorKeywords: [] });
  };
  const resetErrorFilterKeywordsActionOverride = () => {
    setErrorFilterKeywordsActionOverride(null);
  };
  const publishMutationHandlers = {
    onSuccess: resetErrorFilterKeywordsActionOverride,
    onSettled: resetErrorFilterKeywordsActionOverride,
  };
  const executePublish = () => {
    operations?.publish?.mutate(undefined, publishMutationHandlers);
  };

  return useMemo(() => {
    switch (status) {
      case 'draft':
        return {
          type: 'publish',
          label: getActionLabel('PUBLISH', operations?.publish?.actionLabel),
          disabled: !(
            isEntitySaved && Boolean(operations?.publish?.enableAction)
          ),
          modal:
            !isDeepPublishAvailable && publishDangerously
              ? {
                  type: 'confirmation',
                  title: formatMessage(
                    { id: 'PUBLISH_DANGEROUSLY_MODAL_TITLE' },
                    { environment: environmentName },
                  ),
                  description: formatMessage(
                    {
                      id: 'PUBLISH_DANGEROUSLY_MODAL_DESCRIPTION',
                    },
                    {
                      entity: entityConfigName,
                      environment: environmentName,
                    },
                  ),
                  confirmCtaText: formatMessage(
                    {
                      id: 'PUBLISH_DANGEROUSLY_MODAL_PRIMARY_CTA',
                    },
                    {
                      environment: environmentName,
                      entity: entityConfigName,
                    },
                  ),
                  checkboxConfirmationLabel: formatMessage(
                    {
                      id: 'PUBLISH_DANGEROUSLY_MODAL_CHECKBOX',
                    },
                    {
                      environment: environmentName,
                      entity: entityConfigName,
                    },
                  ),
                  confirmCtaAction: executePublish,
                  onBeforeOpen: validateBeforePublish,
                }
              : undefined,
          display: Boolean(operations?.publish?.enableAction) && !isReadOnly,
          action: () => {
            if (!validateBeforePublish()) {
              return;
            }

            if (isDeepPublishAvailable) {
              setIsDeepPublishModalOpen?.(true);
            } else {
              executePublish();
            }
          },
        };
      case 'published':
        return {
          type: 'republish',
          label: getActionLabel(
            'PUBLISH_CHANGES',
            operations?.republish?.actionLabel,
          ),
          // Disable publishing/updating when the entity is readonly
          display: Boolean(operations?.republish?.enableAction) && !isReadOnly,
          disabled: isEntitySaved,
          action: () => {
            if (validateBeforePublish()) {
              operations?.republish?.mutate(undefined, publishMutationHandlers);
            }
          },
        };
      case 'archived':
        return {
          type: 'publish',
          label: getActionLabel('PUBLISH', operations?.publish?.actionLabel),
          display: false,
        };
      case 'new':
      default:
        return domainEntity.draftingEnabled === false
          ? {
              type: 'publish',
              label: getActionLabel(
                'PUBLISH',
                getEntityOperationConfig(domainEntity, 'create')?.actionLabel ??
                  (domainEntity.isUsingConfigAppBackend === false
                    ? domainEntity.endpoints.create?.actionLabel
                    : undefined) ??
                  operations?.create?.actionLabel,
              ),
              display: true,
              action: () => {
                if (validateBeforePublish()) {
                  operations?.create?.mutate();
                }
              },
            }
          : {
              type: 'publish',
              label: getActionLabel(
                'PUBLISH',
                operations?.publish?.actionLabel,
              ),
              display: false,
            };
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    entityConfigName,
    formatMessage,
    isEntitySaved,
    isReadOnly,
    publishDangerously,
    operations?.create?.actionLabel,
    operations?.create?.actionType,
    operations?.create?.mutate,
    operations?.publish?.enableAction,
    operations?.publish?.mutate,
    operations?.republish?.enableAction,
    operations?.republish?.mutate,
    status,
    setErrorFilterKeywordsActionOverride,
    environmentName,
    domainEntity.draftingEnabled,
    isDeepPublishAvailable,
    setIsDeepPublishModalOpen,
    validateBeforePublish,
    executePublish,
    publishMutationHandlers,
  ]);
};
